import { ArrowDown01Icon, Cancel01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import * as React from 'react';

import { Button } from './button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { type DataState, dataStateSurfaceVariants } from './data-state-label';
import { DatumLabel } from './datum';
import { Dialog, DialogContent, DialogFooter } from './dialog';
import { cn } from './lib/utils';

/**
 * Scaffold for high-consequence confirmation modals (workload categories,
 * bulk applies, release gates). Composition over configuration: the title
 * names the action (DialogHeader/DialogTitle), the body leads with the
 * highest-impact consequence, counts are split per data state, and detail
 * tables are collapsed by default — the confirm button never requires
 * expanding them.
 */
function ConfirmActionModal({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog {...props}>
      <DialogContent
        data-slot="confirm-action-modal"
        className={cn('max-h-[85vh] gap-4 overflow-y-auto sm:max-w-lg', className)}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** The headline consequence, tinted by the data state it derives from. */
function ConfirmActionConsequence({
  className,
  state,
  ...props
}: React.ComponentProps<'div'> & { state: DataState }) {
  return (
    <div
      data-slot="confirm-action-consequence"
      data-state={state}
      className={cn(
        'rounded-md px-3 py-2 font-mono text-[13px] tabular-nums',
        dataStateSurfaceVariants({ state }),
        className,
      )}
      {...props}
    />
  );
}

/** A labelled section (e.g. one per data state in a bulk-apply summary). */
function ConfirmActionSection({
  className,
  label,
  children,
  ...props
}: React.ComponentProps<'div'> & { label: React.ReactNode }) {
  return (
    <div
      data-slot="confirm-action-section"
      className={cn('flex flex-col gap-1.5', className)}
      {...props}
    >
      <DatumLabel>{label}</DatumLabel>
      {children}
    </div>
  );
}

/** Explicit CANNOT / CAN lists for mixed-consequence confirmations. */
function ConfirmActionCapabilities({
  className,
  cannot,
  can,
  ...props
}: React.ComponentProps<'div'> & {
  cannot?: React.ReactNode[];
  can?: React.ReactNode[];
}) {
  return (
    <div
      data-slot="confirm-action-capabilities"
      className={cn('flex flex-col gap-3', className)}
      {...props}
    >
      {cannot !== undefined && cannot.length > 0 && (
        <div className="flex flex-col gap-1">
          <DatumLabel>Cannot</DatumLabel>
          <ul className="flex flex-col gap-1">
            {cannot.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <HugeiconsIcon
                  icon={Cancel01Icon}
                  strokeWidth={2}
                  className="text-destructive mt-0.5 size-3.5 shrink-0"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
      {can !== undefined && can.length > 0 && (
        <div className="flex flex-col gap-1">
          <DatumLabel>Can</DatumLabel>
          <ul className="flex flex-col gap-1">
            {can.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <HugeiconsIcon
                  icon={Tick02Icon}
                  strokeWidth={2}
                  className="text-cat-1 mt-0.5 size-3.5 shrink-0"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible detail table (e.g. the affected work-order list). Collapsed
 * by default — confirming never requires expanding it.
 */
function ConfirmActionDetails({
  className,
  summary,
  defaultOpen = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  summary: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} data-slot="confirm-action-details" {...props}>
      <CollapsibleTrigger
        className={cn(
          'group flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground',
          className,
        )}
      >
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          strokeWidth={2}
          className="size-3.5 transition-transform group-data-[state=open]:rotate-180"
        />
        {summary}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function ConfirmActionFooter({
  className,
  ownership,
  cancelLabel = 'Cancel',
  confirmLabel,
  onCancel,
  onConfirm,
  confirmDisabled = false,
  pending = false,
  ...props
}: React.ComponentProps<'div'> & {
  /** Ownership language, e.g. "You are responsible for all confirmed values." */
  ownership?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  pending?: boolean;
}) {
  return (
    <div
      data-slot="confirm-action-footer"
      className={cn('flex flex-col gap-3', className)}
      {...props}
    >
      {ownership !== undefined && (
        <p data-slot="confirm-action-ownership" className="text-muted-foreground text-xs">
          {ownership}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button type="button" onClick={onConfirm} disabled={confirmDisabled || pending}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}

export {
  ConfirmActionCapabilities,
  ConfirmActionConsequence,
  ConfirmActionDetails,
  ConfirmActionFooter,
  ConfirmActionModal,
  ConfirmActionSection,
};
